import { Company, PaymentOption, ShippingPreferences } from '@/models/company';
import notify from '@/notify';
import i18n from '@/plugins/i18n';
import api from '@/services/api';
import { isHttpError } from '@/utils/errors';

import { CompanyState, getInitialBadgeCountState } from './company.state';

interface FetchCompanyActionPayload {
  companyId: number;
  isBrandOwner?: boolean;
}

interface UpdateCompanyActionPayload extends Partial<Company> {
  companyId: number;
  businessIdentificationNumber?: string;
  addressesConfirmed?: number;
}

interface UpdateCompanySystemsPayload {
  companyId: number;
  erpSystem: string;
  erpSystemOther?: string;
  posSystem: string;
  posSystemOther?: string;
  accountingSystem: string;
  accountingSystemOther?: string;
  crmSystem: string;
  crmSystemOther?: string;
  reportingSystem: string;
  reportingSystemOther?: string;
  itSystem: string;
  itSystemOther?: string;
  s2STrackingSystem: string;
  s2STrackingSystemOther?: string;
  logisticsSystem: string;
  logisticsSystemOther?: string;
  integrateSystem?: string;
}

interface UpdateMetrcKeyActionPayload {
  companyId: number;
  metrcKeyData: {
    shippingMetrcKey: string;
  };
}

export const setupCompanyActions = (state: CompanyState) => {
  const setActiveCompanyInState = (company: Company | undefined) => {
    if (!company) {
      return;
    }

    state.activeCompanyId.value = company.id;
    state.activeCompany.value = company;

    const index = state.companies.value.findIndex((company) => company.id === company.id);

    if (!~index) {
      state.companies.value.unshift(company);
    } else {
      state.companies.value.splice(index, 1, company);
    }
  };

  const fetchBadgeCounts = async () => {
    const badgeCounts = await api.company.getBadgeCounts();

    state.badgeCounts.value = badgeCounts || getInitialBadgeCountState();

    return badgeCounts;
  };

  const fetchCompanies = async () => {
    try {
      state.isLoadingCompanies.value = true;

      const response = await api.company.getCompanies();

      state.companies.value = response.results;
    } finally {
      state.isLoadingCompanies.value = false;
    }
  };

  const fetchCompany = async (payload: FetchCompanyActionPayload) => {
    try {
      state.isLoadingCompanies.value = true;

      const companyDetails = await api.company.getDetails(payload);

      if (!companyDetails) {
        state.isLoadingCompanies.value = false;
        return;
      }

      setActiveCompanyInState(companyDetails);

      return companyDetails;
    } finally {
      state.isLoadingCompanies.value = false;
    }
  };

  const fetchCompanyPaymentProgramChangeRequests = async () => {
    try {
      const paymentProgramChangeRequests = await api.company.getCompanyPaymentProgramChangeRequests();

      state.paymentProgramChangeRequests.value = paymentProgramChangeRequests;
    } catch (error) {
      if (isHttpError(error) && error.response?.status === 402) {
        return; // the delinquencyGuard in router/guards.ts will redirect the user to update their billing info
      } else {
        throw error;
      }
    }
  };

  const updateCompanyPaymentProgramChangeRequest = async (payload: string[]) => {
    await api.company.updateCompanyPaymentProgramChangeRequest(payload);
  };

  const fetchCompanyPaymentOptions = async (payload: { id?: number; active?: boolean }) => {
    state.isLoadingPaymentOptions.value = true;

    const response = await api.company.getCompanyPaymentOptions(payload);

    state.paymentOptions.value = response;
    state.isLoadingPaymentOptions.value = false;

    return response;
  };

  const fetchCompanyShippingPreferences = async () => {
    return await api.company.getCompanyShippingPreferences();
  };

  const fetchCompanyStaff = async () => {
    const response = await api.company.getCompanyStaff();

    state.staff.value = response.results;

    return response;
  };

  const fetchHighlights = async () => {
    const response = await api.company.getHighlights();

    return response.results;
  };

  const fetchSellers = async (payload: { name: string }) => {
    return await api.company.getSellers(payload);
  };

  const saveCompanyPaymentOptions = async (payload: PaymentOption) => {
    const response = await api.company.saveCompanyPaymentOptions(payload);

    notify.success(i18n.t('settings.payments.notify.paymentOptionsSaved'));

    state.paymentOptions.value = response;

    return response;
  };

  const updateCompany = async ({ companyId, ...params }: UpdateCompanyActionPayload) => {
    const updatedCompany: Company = await api.company.updateCompany(companyId, params);

    setActiveCompanyInState(updatedCompany);

    if (params.addressesConfirmed) {
      notify.success(i18n.t('cart.notify.addressedConfirmed'));
    }

    return updatedCompany;
  };

  const updateCompanySystems = async (companyId: number, payload: UpdateCompanySystemsPayload) => {
    const updatedCompany: Company = await api.company.updateCompany(companyId, payload);

    setActiveCompanyInState(updatedCompany);

    return updatedCompany;
  };

  const updateCompanyShippingPreferences = async (payload: Partial<ShippingPreferences>) => {
    const updatedShippingPreferences: ShippingPreferences = await api.company.updateCompanyShippingPreferences(payload);

    return updatedShippingPreferences;
  };

  const updateMetrcKey = async (payload: UpdateMetrcKeyActionPayload) => {
    const updatedCompany = await api.company.updateMetrcKey(payload);

    setActiveCompanyInState(updatedCompany);
  };

  return {
    fetchBadgeCounts,
    fetchCompanies,
    fetchCompany,
    fetchCompanyPaymentProgramChangeRequests,
    fetchCompanyPaymentOptions,
    fetchCompanyShippingPreferences,
    fetchCompanyStaff,
    fetchHighlights,
    fetchSellers,
    saveCompanyPaymentOptions,
    updateCompany,
    updateCompanySystems,
    updateCompanyShippingPreferences,
    updateMetrcKey,
    updateCompanyPaymentProgramChangeRequest,
  };
};
